import { EntityTypeEnum, SubscriptionFilter, SubscriptionIdentifier } from 'api'
import { SelectionStore, WindowSelections } from './selectionTypes'
import { SelectionActions, SelectionActionTypes } from './selectionActions'
import { GridId } from '../grid'
import { getEnumValues } from 'model'

export const initialState: SelectionStore = {
  windows: {},
  activityMonitorOpen: false,
  activityMonitorLastClosed: new Date(),
  gridColumnsAutoSized: getEnumValues(GridId).reduce((result, gridId) => {
    result[gridId] = false
    return result
  }, {} as Record<GridId, boolean>),
  entitySubscriptions: getEnumValues(EntityTypeEnum).reduce((result, entityType) => {
    result[entityType] = {}
    return result
  }, {} as Record<EntityTypeEnum, Record<SubscriptionIdentifier, SubscriptionFilter[] | undefined>>)
}

export const initialWindowSelections: WindowSelections = {
  preparedStagingOrders: [],
  preparedCollectionId: null,
  selectedGridSettings: getEnumValues(GridId).reduce((result, gridId) => {
    result[gridId] = 'default'
    return result
  }, {} as Record<GridId, string>),
  leftNavigationMinimised: false,
  prepareRFQPanelPinned: false,
  auditSubscriptions: [],
  organisationId: null,
  openOrganisationIds: [],
  pendingCollectionCorrelationId: null,
  selectedCollectionIds: {} as Record<GridId, string>,
  selectedCounterpartyIds: {} as Record<GridId, string>,
  collapsedNavigationEntries: {} as Record<string, boolean>,
  currentGridId: null,
  gridActivityHighlight: {} as Record<GridId, boolean>,
  gridRowIdInEdit: null,
  gridRowInEdit: null,
  dealIdForRelatedDealTrades: null,
  dealIdForRelatedOpenRepoTrades: null,
  dealIdForRelatedStructuredTrades: null,
  dealIdForRelatedMidTradeInitiatedTrades: null,
  dealIdForRelatedMidTradeIncomingTrades: null
}

export function selectionReducer(state: SelectionStore = initialState, action: SelectionActions): SelectionStore {
  switch (action.type) {
    case SelectionActionTypes.OPEN_ACTIVITY_MONITOR: {
      return {
        ...state,
        activityMonitorOpen: action.payload.activityMonitorOpen,
        activityMonitorLastClosed: !action.payload.activityMonitorOpen ? new Date() : state.activityMonitorLastClosed
      }
    }

    case SelectionActionTypes.SET_COLUMNS_AUTO_SIZED: {
      return state.gridColumnsAutoSized[action.payload.gridId] === action.payload.autoSized
        ? state
        : {
            ...state,
            gridColumnsAutoSized: {
              ...state.gridColumnsAutoSized,
              [action.payload.gridId]: action.payload.autoSized
            }
          }
    }

    case SelectionActionTypes.INITIALISE_WINDOW: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: { ...initialWindowSelections, ...action.payload.selections }
        }
      }
    }

    case SelectionActionTypes.SET_PREPARED_STAGING_ORDERS: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            preparedStagingOrders: action.payload.preparedStagingOrders
          }
        }
      }
    }

    case SelectionActionTypes.SET_PREPARED_COLLECTION_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            preparedCollectionId: action.payload.preparedCollectionId
          }
        }
      }
    }

    case SelectionActionTypes.UNPREPARE_STAGING_ALL: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            preparedStagingOrders: []
          }
        }
      }
    }

    case SelectionActionTypes.SELECT_CURRENT_GRID_CONFIG: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedGridSettings: {
              ...state.windows[windowId].selectedGridSettings,
              [action.payload.gridId]: action.payload.gridSettingsId
            }
          }
        }
      }
    }

    case SelectionActionTypes.TOGGLE_LEFT_NAVIGATION: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            leftNavigationMinimised: !state.windows[windowId].leftNavigationMinimised
          }
        }
      }
    }

    case SelectionActionTypes.TOGGLE_PINNED_PREPARE_RFQ_PANEL: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            prepareRFQPanelPinned: action.payload.isPinned
          }
        }
      }
    }

    case SelectionActionTypes.ADD_AUDIT_SUBSCRIPTIONS: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            auditSubscriptions: [...new Set([...state.windows[windowId].auditSubscriptions, ...action.payload.orderIds])]
          }
        }
      }
    }

    case SelectionActionTypes.REMOVE_AUDIT_SUBSCRIPTIONS: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            auditSubscriptions: state.windows[windowId].auditSubscriptions.filter(
              auditSubscription => !action.payload.orderIds.includes(auditSubscription)
            )
          }
        }
      }
    }

    case SelectionActionTypes.CLEAR_AUDIT_SUBSCRIPTIONS: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            auditSubscriptions: []
          }
        }
      }
    }

    case SelectionActionTypes.SET_ORGANISATION_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            organisationId: action.payload.organisationId
          }
        }
      }
    }

    case SelectionActionTypes.OPEN_ORGANISATION_PROFILES: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            openOrganisationIds: [...state.windows[windowId].openOrganisationIds, ...action.payload.organisationIds]
          }
        }
      }
    }

    case SelectionActionTypes.CLOSE_ORGANISATION_PROFILES: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            openOrganisationIds: state.windows[windowId].openOrganisationIds.filter(
              organisationId => !action.payload.organisationIds.includes(organisationId)
            )
          }
        }
      }
    }

    case SelectionActionTypes.SET_COLLECTION_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedCollectionIds: {
              ...state.windows[windowId].selectedCollectionIds,
              [action.payload.gridId]: action.payload.collectionId
            }
          }
        }
      }
    }

    case SelectionActionTypes.CLEAR_COLLECTION_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedCollectionIds: {
              ...state.windows[windowId].selectedCollectionIds,
              [action.payload.gridId]: undefined
            }
          }
        }
      }
    }

    case SelectionActionTypes.CLEAR_ALL_COLLECTION_IDS: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedCollectionIds: {} as Record<GridId, string>
          }
        }
      }
    }

    case SelectionActionTypes.SET_COUNTERPARTY_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedCounterpartyIds: {
              ...state.windows[windowId].selectedCounterpartyIds,
              [action.payload.gridId]: action.payload.counterpartyId
            }
          }
        }
      }
    }

    case SelectionActionTypes.CLEAR_COUNTERPARTY_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            selectedCounterpartyIds: {
              ...state.windows[windowId].selectedCounterpartyIds,
              [action.payload.gridId]: undefined
            }
          }
        }
      }
    }

    case SelectionActionTypes.SET_NAVIGATION_COLLAPSED: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            collapsedNavigationEntries: {
              ...state.windows[windowId].collapsedNavigationEntries,
              [action.payload.navigationEntry]: action.payload.collapsed
            }
          }
        }
      }
    }

    case SelectionActionTypes.SET_CURRENT_GRID_ID: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            currentGridId: action.payload.gridId
          }
        }
      }
    }

    case SelectionActionTypes.SET_GRID_ACTIVITY_HIGHLIGHT: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            gridActivityHighlight: {
              ...state.windows[windowId].gridActivityHighlight,
              [action.payload.gridId]: action.payload.showHighlight
            }
          }
        }
      }
    }

    case SelectionActionTypes.SET_GRID_ROW_IN_EDIT: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            gridRowIdInEdit: action.payload.rowIndex,
            gridRowInEdit: action.payload.rowData
          }
        }
      }
    }

    case SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_DEAL_TRADES: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            dealIdForRelatedDealTrades: action.payload.dealId
          }
        }
      }
    }

    case SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_OPEN_REPO_TRADES: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            dealIdForRelatedOpenRepoTrades: action.payload.dealId
          }
        }
      }
    }

    case SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_STRUCTURED_TRADES: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            dealIdForRelatedStructuredTrades: action.payload.dealId
          }
        }
      }
    }

    case SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INITIATED: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            dealIdForRelatedMidTradeInitiatedTrades: action.payload.dealId
          }
        }
      }
    }

    case SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INCOMING: {
      const windowId = action.payload ? action.payload.windowId : ''
      return {
        ...state,
        windows: {
          ...state.windows,
          [windowId]: {
            ...state.windows[windowId],
            dealIdForRelatedMidTradeIncomingTrades: action.payload.dealId
          }
        }
      }
    }

    case SelectionActionTypes.ADD_ENTITY_SUBSCRIPTION: {
      return {
        ...state,
        entitySubscriptions: {
          ...state.entitySubscriptions,
          [action.payload.entityType]: {
            ...state.entitySubscriptions[action.payload.entityType],
            [action.payload.subscriptionIdentifier]: {
              ...state.entitySubscriptions[action.payload.entityType][action.payload.subscriptionIdentifier],
              filters: action.payload.subscriptionFilters
            }
          }
        }
      }
    }

    case SelectionActionTypes.REMOVE_ENTITY_SUBSCRIPTION: {
      const newEntitySubscriptions = { ...state.entitySubscriptions[action.payload.entityType] }
      delete newEntitySubscriptions[action.payload.subscriptionIdentifier]
      return {
        ...state,
        entitySubscriptions: {
          ...state.entitySubscriptions,
          [action.payload.entityType]: newEntitySubscriptions
        }
      }
    }

    case SelectionActionTypes.ADD_SUBSCRIPTION_PAGE_DETAILS: {
      return {
        ...state,
        entitySubscriptions: {
          ...state.entitySubscriptions,
          [action.payload.entityType]: {
            ...state.entitySubscriptions[action.payload.entityType],
            [action.payload.subscriptionIdentifier]: {
              ...state.entitySubscriptions[action.payload.entityType][action.payload.subscriptionIdentifier],
              page: action.payload.page,
              totalPages: action.payload.totalPages,
              resultCount: action.payload.resultCount,
              totalResultCount: action.payload.totalResultCount
            }
          }
        }
      }
    }

    case SelectionActionTypes.EDIT_SUBSCRIPTION_PAGE_DETAILS: {
      return {
        ...state,
        entitySubscriptions: {
          ...state.entitySubscriptions,
          [action.payload.entityType]: {
            ...state.entitySubscriptions[action.payload.entityType],
            [action.payload.subscriptionIdentifier]: {
              ...state.entitySubscriptions[action.payload.entityType][action.payload.subscriptionIdentifier],
              page: action.payload.page,
              totalPages: action.payload.totalPages
            }
          }
        }
      }
    }

    default: {
      return state
    }
  }
}
